/**
 * WeatherService — Open-Meteo client with stale-while-revalidate caching.
 *
 * Strategy (post-PR-A migration 2026-04-30):
 *   - All caching now goes through `swr()` keyed by lat/lng. Hot cache: 30 min.
 *     Stale tolerance: 24 h (offline fallback).
 *   - `fetch()` triggers swr; widget gets fresh-or-stale instantly.
 *   - When swr revalidates in the background, `onUpdate` fires and the
 *     in-memory `this.data` is replaced. Widgets that have the service
 *     reference call `getData()` to read the latest.
 *   - `getData()` is sync, never throws, returns null when there's no cache
 *     yet (widget should render an empty state).
 */

import type { WeatherData } from '../modules/home/types';
import type { LocationService } from './LocationService';
import { swr, swrPeek } from './swr';

const FRESH_TTL_MS = 30 * 60 * 1000; // 30 min — hot cache
const STALE_TTL_MS = 24 * 60 * 60 * 1000; // 24 h — offline tolerance

function cacheKey(lat: number, lng: number): string {
  // Round to ~1 km to share cache across nearby coords (privacy + cache hits).
  return `weather:${lat.toFixed(2)}:${lng.toFixed(2)}`;
}

export class WeatherService {
  private data: WeatherData | null = null;
  private locationService: LocationService;

  constructor(locationService: LocationService) {
    this.locationService = locationService;
    this.loadFromCache();
  }

  getData(): WeatherData | null {
    return this.data;
  }

  /** Sync read of any swr-cached entry for the current location. */
  private loadFromCache(): void {
    const loc = this.locationService.getState();
    if (loc.lat === null || loc.lng === null) return;
    const cached = swrPeek<WeatherData>(cacheKey(loc.lat, loc.lng));
    if (cached?.data) {
      this.data = cached.data;
    }
  }

  /**
   * Fetch with stale-while-revalidate. Returns cached value instantly when
   * available; never blocks the caller waiting on network. Background
   * revalidation updates `this.data` so the next `getData()` reflects the
   * fresh value.
   */
  async fetch(): Promise<WeatherData | null> {
    const loc = this.locationService.getState();
    if (loc.lat === null || loc.lng === null) return null;

    const key = cacheKey(loc.lat, loc.lng);
    const result = await swr<WeatherData>(
      key,
      () => this._fetchFromNetwork(loc.lat!, loc.lng!, loc.city || undefined),
      {
        freshTtlMs: FRESH_TTL_MS,
        staleTtlMs: STALE_TTL_MS,
        onUpdate: (data) => { this.data = data; },
      },
    );
    if (result) this.data = result;
    return this.data;
  }

  /** Raw Open-Meteo network call. Called only by swr when cache is stale. */
  private async _fetchFromNetwork(lat: number, lng: number, city?: string): Promise<WeatherData | null> {
    const url =
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
      `&current=temperature_2m,apparent_temperature,rain,relative_humidity_2m,wind_speed_10m` +
      `&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,precipitation_sum` +
      `&hourly=rain` +
      `&forecast_days=1` +
      `&timezone=auto`;

    const resp = await fetch(url, { cache: 'no-store' });
    if (!resp.ok) return null;
    const json = await resp.json();
    const current = json.current;
    const daily = json.daily;

    const hourlyRain: { hour: number; rain: number }[] = [];
    if (json.hourly?.rain && json.hourly?.time) {
      const times = json.hourly.time as string[];
      const rains = json.hourly.rain as number[];
      for (let i = 0; i < Math.min(24, times.length); i++) {
        hourlyRain.push({ hour: i, rain: rains[i] || 0 });
      }
    }

    return {
      temperature: current?.temperature_2m ?? 0,
      feelsLike: current?.apparent_temperature ?? current?.temperature_2m ?? 0,
      tempMax: daily?.temperature_2m_max?.[0] ?? 0,
      tempMin: daily?.temperature_2m_min?.[0] ?? 0,
      rain: current?.rain ?? 0,
      humidity: current?.relative_humidity_2m ?? 0,
      windSpeed: current?.wind_speed_10m ?? 0,
      precipitationProbability: daily?.precipitation_probability_max?.[0] ?? 0,
      hourlyRain,
      fetchedAt: Date.now(),
      city,
    };
  }
}
